import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { testService } from '../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Star } from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  Legend, ResponsiveContainer, Tooltip
} from 'recharts';

const CANDIDATE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export const ComparerCandidatsPage = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ids = searchParams.get('ids');
    if (ids) {
      fetchComparison(ids.split(','));
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const fetchComparison = async (testIds) => {
    try {
      const response = await testService.compare(testIds);
      setData(response.data);
    } catch (error) {
      toast.error('Erreur lors de la comparaison');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Comparaison" breadcrumbs={['Accueil', 'Comparaison']}>
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-400">Chargement...</p>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout title="Comparaison" breadcrumbs={['Accueil', 'Comparaison']}>
        <div className="text-center py-12">
          <p className="text-slate-400">Aucune donnée de comparaison</p>
        </div>
      </Layout>
    );
  }

  const { candidates, radar_data, questionnaire } = data;

  return (
    <Layout
      title="Comparaison des candidats"
      breadcrumbs={['Accueil', 'Questionnaires', 'Comparaison']}
    >
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <Link
          to="/questionnaires"
          className="text-slate-400 hover:text-white transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h2 className="text-lg font-semibold text-white">
            {questionnaire.fonction} — {questionnaire.niveau}
          </h2>
          <p className="text-sm text-slate-400">
            {questionnaire.client || 'Test générique'}
            {questionnaire.ref_ao && ` — ${questionnaire.ref_ao}`}
            {' • '}
            {candidates.length} candidats comparés
          </p>
        </div>
      </div>

      {/* Candidate Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="candidate-summary-cards">
        {candidates.map((c, i) => (
          <Link
            key={c.id}
            to={`/rapport/${c.id}`}
            className="eva-card hover:shadow-md transition-shadow"
            data-testid={`compare-candidate-${c.id}`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: CANDIDATE_COLORS[i] }}
              />
              <p className="font-semibold text-white">{c.nom}</p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{c.average_score?.toFixed(1)}/10</p>
                <div className="flex gap-0.5 mt-1">
                  {[...Array(10)].map((_, j) => (
                    <Star
                      key={j}
                      size={12}
                      fill={j < Math.round(c.average_score || 0) ? '#F59E0B' : '#E9ECEF'}
                      stroke={j < Math.round(c.average_score || 0) ? '#F59E0B' : '#E9ECEF'}
                    />
                  ))}
                </div>
              </div>
              {c.evaluation && (
                <span className={`text-xs px-2 py-1 rounded-full ${
                  c.evaluation === 'Très bon niveau' ? 'bg-blue-600/20 text-blue-400 border border-blue-500/20' :
                  c.evaluation === 'Au-dessus de la moyenne' ? 'bg-[#10B981] bg-opacity-15 text-[#10B981]' :
                  c.evaluation === 'Dans la moyenne' ? 'bg-[#F59E0B] bg-opacity-15 text-[#F59E0B]' :
                  'bg-[#EF4444] bg-opacity-15 text-[#EF4444]'
                }`}>{c.evaluation}</span>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {c.date ? new Date(c.date).toLocaleDateString('fr-FR') : ''}
            </p>
          </Link>
        ))}
      </div>

      {/* Overlaid Radar Chart */}
      <div className="eva-card mb-8" data-testid="comparison-radar-chart">
        <h3 className="text-lg font-semibold text-white mb-4">
          Comparaison des compétences
        </h3>
        <ResponsiveContainer width="100%" height={450}>
          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radar_data}>
            <PolarGrid stroke="#E9ECEF" />
            <PolarAngleAxis
              dataKey="label"
              tick={{ fill: '#495057', fontSize: 12 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 10]}
              tickCount={6}
              tick={{ fill: '#6C757D', fontSize: 11 }}
            />
            {candidates.map((c, i) => (
              <Radar
                key={c.id}
                name={c.nom}
                dataKey={`candidat_${i}`}
                stroke={CANDIDATE_COLORS[i]}
                fill={CANDIDATE_COLORS[i]}
                fillOpacity={0.1}
                strokeWidth={2}
                dot={{ r: 4, fill: CANDIDATE_COLORS[i] }}
              />
            ))}
            <Legend wrapperStyle={{ fontSize: '14px', color: '#495057' }} />
            <Tooltip
              contentStyle={{
                background: 'white',
                border: '1px solid #E9ECEF',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              formatter={(value) => `${value}/10`}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Comparison Table */}
      <div className="eva-card" data-testid="comparison-table">
        <h3 className="text-lg font-semibold text-white mb-4">
          Détail par compétence
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 font-semibold text-slate-400 uppercase tracking-wider text-xs">
                  Compétence
                </th>
                {candidates.map((c, i) => (
                  <th key={c.id} className="text-center py-3 px-4 font-semibold text-xs uppercase tracking-wider" style={{ color: CANDIDATE_COLORS[i] }}>
                    {c.nom}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {radar_data.map((row, ri) => (
                <tr key={ri} className="border-b border-[#F8F9FA] hover:bg-white/5 transition-colors">
                  <td className="py-3 px-4 font-medium text-white">{row.label}</td>
                  {candidates.map((c, ci) => {
                    const score = row[`candidat_${ci}`] || 0;
                    const maxInRow = Math.max(...candidates.map((_, k) => row[`candidat_${k}`] || 0));
                    const isBest = score > 0 && score === maxInRow;
                    return (
                      <td key={c.id} className="text-center py-3 px-4">
                        <span className={`inline-block px-2 py-1 rounded ${
                          isBest ? 'bg-emerald-500/15 text-[#10B981] font-semibold' :
                          score === 0 ? 'text-[#E9ECEF]' : 'text-slate-300'
                        }`}>
                          {score > 0 ? `${score}/10` : '—'}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* Average row */}
              <tr className="border-t-2 border-white/10 bg-white/5">
                <td className="py-3 px-4 font-bold text-white">Moyenne</td>
                {candidates.map((c, i) => {
                  const maxAvg = Math.max(...candidates.map(x => x.average_score || 0));
                  const isBest = c.average_score > 0 && c.average_score === maxAvg;
                  return (
                    <td key={c.id} className="text-center py-3 px-4">
                      <span className={`font-bold ${isBest ? 'text-[#10B981]' : 'text-white'}`}>
                        {c.average_score?.toFixed(1)}/10
                      </span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};
